import { URL_AUTHORIZE_STG, SCOPE } from './constants';
/**
 * This component implements the 3-legged token with Authorization Code Grant (PKCE) mechanism.
 * Documentation: https://aps.autodesk.com/en/docs/oauth/v2/tutorials/get-3-legged-token-pkce/get-3-legged-token-pkce/
 */
export class AuthorizationCodeGrantComponent {
  constructor(options) {
    this.clientId = options.clientId;
    this.callbackUrl = options.callbackUrl;
  }

  getBearerToken = () => {
    return new Promise((resolve, reject) => {
      const authorizationCode = this.extractUrlValue('code');

      if (!authorizationCode) {
        this.generatePKCEChallenge()
          .then(({ codeVerifier, codeChallenge }) => {
            sessionStorage.setItem('code_verifier', codeVerifier);

            const authUrl = new URL(URL_AUTHORIZE_STG);
            authUrl.searchParams.append('response_type', 'code');
            authUrl.searchParams.append('client_id', this.clientId);
            authUrl.searchParams.append('redirect_uri', this.callbackUrl);
            authUrl.searchParams.append('scope', SCOPE);
            authUrl.searchParams.append('nonce', crypto.randomUUID());
            authUrl.searchParams.append('code_challenge', codeChallenge);
            authUrl.searchParams.append('code_challenge_method', 'S256');

            window.location.replace(authUrl.toString());
          })
          .catch(reject);
      } else {
        window.top.history.replaceState(null, 'Application', this.callbackUrl);
        this.extractCodeForToken(authorizationCode)
          .then((accessToken) => {
            resolve(accessToken);
          })
          .catch(reject);
      }
    });
  };

  extractCodeForToken = (authorizationCode) => {
    const codeVerifier = sessionStorage.getItem('code_verifier');

    return fetch('/api/oauth/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        code: authorizationCode,
        code_verifier: codeVerifier,
        redirect_uri: this.callbackUrl,
        client_id: this.clientId,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Token request failed');
        }
        return response.json();
      })
      .then((data) => {
        sessionStorage.removeItem('code_verifier');
        return data.access_token;
      })
      .catch((error) => {
        sessionStorage.removeItem('code_verifier');
        throw error;
      });
  };

  extractUrlValue = (value) => {
    const urlQuery = window.top.location.search.substring(1);

    const variableArray = urlQuery.split('&');
    for (let i = 0, length = variableArray.length; i < length; ++i) {
      const pair = variableArray[i].split('=');
      if (decodeURIComponent(pair[0]) === value) {
        return decodeURIComponent(pair[1]);
      }
    }
    return null;
  };

  base64URLEncode = (arrayBuffer) => {
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return this.base64URLEncode(array.buffer);
  };

  generateCodeChallenge = async (codeVerifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return this.base64URLEncode(hash);
  };

  generatePKCEChallenge = async () => {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    return {
      codeVerifier,
      codeChallenge,
    };
  };
}

export default AuthorizationCodeGrantComponent;
